import { z } from 'zod';

/**
 * zod 4 rewrote its default error messages ("Required" became "Invalid input: expected string, received undefined").
 * Our forms show these messages to users, so this error map keeps the zod 3 wording.
 * It only applies when a schema doesn't define its own message.
 */
function receivedType(input: unknown): string {
  if (input === null) return 'null';
  if (Array.isArray(input)) return 'array';
  if (typeof input === 'number') return Number.isNaN(input) ? 'nan' : Number.isFinite(input) ? 'number' : 'infinity';
  if (input instanceof Date) return 'date';
  return typeof input;
}

function quote(value: unknown): string {
  return typeof value === 'string' ? `'${value}'` : String(value);
}

const PRIMITIVE_TYPES = ['string', 'number', 'boolean', 'bigint', 'symbol', 'undefined', 'null', 'object', 'array', 'date', 'function'];

const zodV3ErrorMessages: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type':
      if (issue.input === undefined) return 'Required';
      if (issue.expected === 'int') return 'Expected integer, received float';
      if (!PRIMITIVE_TYPES.includes(issue.expected)) return `Input not instance of ${issue.expected}`;
      return `Expected ${issue.expected}, received ${receivedType(issue.input)}`;
    case 'invalid_value':
      if (issue.input === undefined) return 'Required';
      return `Invalid enum value. Expected ${issue.values.map(quote).join(' | ')}, received ${quote(issue.input)}`;
    case 'too_small': {
      const { minimum, inclusive, exact } = issue;
      if (issue.origin === 'string')
        return `String must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'over'} ${minimum} character(s)`;
      if (issue.origin === 'number')
        return `Number must be ${exact ? 'exactly equal to ' : inclusive ? 'greater than or equal to ' : 'greater than '}${minimum}`;
      if (issue.origin === 'array')
        return `Array must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'more than'} ${minimum} element(s)`;
      return 'Invalid input';
    }
    case 'too_big': {
      const { maximum, inclusive, exact } = issue;
      if (issue.origin === 'string')
        return `String must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'under'} ${maximum} character(s)`;
      if (issue.origin === 'number')
        return `Number must be ${exact ? 'exactly' : inclusive ? 'less than or equal to' : 'less than'} ${maximum}`;
      if (issue.origin === 'array')
        return `Array must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'less than'} ${maximum} element(s)`;
      return 'Invalid input';
    }
    case 'invalid_format':
      return `Invalid ${issue.format}`;
    case 'custom':
      return 'Invalid input';
    default:
      // Anything else keeps zod 4's own message
      return undefined;
  }
};

export function initZod() {
  z.config({ customError: zodV3ErrorMessages });
}
